import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  MapPin,
  Plus,
  Check,
  Trash2,
  Loader2,
  Building2,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export const LocationsTab = ({ onSwitched }) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [busySlug, setBusySlug] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dispensary/locations");
      setLocations(data.locations);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const switchTo = async (slug) => {
    setBusySlug(slug);
    try {
      await api.post(`/dispensary/locations/${slug}/activate`);
      await refresh();
      onSwitched && (await onSwitched());
      toast.success("Switched location");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusySlug(null);
    }
  };

  const remove = async (l) => {
    if (
      !window.confirm(
        `Delete "${l.name}"? This removes the listing and all of its products. This cannot be undone.`
      )
    )
      return;
    setBusySlug(l.slug);
    try {
      await api.delete(`/dispensary/locations/${l.slug}`);
      toast.success("Location removed");
      await refresh();
      onSwitched && (await onSwitched());
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusySlug(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="eyebrow mb-1 flex items-center gap-2">
            <Building2 className="w-3.5 h-3.5 text-accent" />
            Locations
          </div>
          <h3 className="font-serif text-2xl">
            One account · {locations.length}{" "}
            {locations.length === 1 ? "shop" : "shops"}
          </h3>
          <p className="text-sm text-foreground/60 max-w-xl mt-1">
            Each location has its own menu, deals, integration and analytics.
            Switch between them anytime from the header.
          </p>
        </div>
        <Button
          data-testid="locations-add-btn"
          onClick={() => setShowAdd(true)}
          className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-6"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add a location
        </Button>
      </div>

      {loading ? (
        <div className="text-muted-foreground text-sm">Loading…</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {locations.map((l) => (
            <div
              key={l.slug}
              data-testid={`locations-row-${l.slug}`}
              className={`border rounded-2xl p-5 transition-colors ${
                l.is_active
                  ? "border-emerald-500/40 bg-emerald-500/5"
                  : "border-border bg-card"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {l.is_active && (
                      <span className="pill text-[10px] bg-emerald-500/15 text-emerald-300 border-emerald-500/40">
                        <Check className="w-3 h-3" />
                        Active
                      </span>
                    )}
                  </div>
                  <div className="font-serif text-lg truncate">{l.name}</div>
                  <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                    <MapPin className="w-3 h-3" />
                    {l.address || "Address pending"} · {l.city || "—"}
                  </div>
                  <div className="text-xs text-muted-foreground mt-2 flex flex-wrap gap-3">
                    <span>{l.products_count} products</span>
                    {l.pickup && <span>Pickup</span>}
                    {l.delivery && <span>Delivery</span>}
                  </div>
                </div>
              </div>

              <div className="mt-4 flex gap-2">
                {!l.is_active && (
                  <Button
                    data-testid={`locations-switch-${l.slug}`}
                    onClick={() => switchTo(l.slug)}
                    disabled={busySlug === l.slug}
                    variant="outline"
                    className="rounded-full bg-transparent border-border h-8 px-4 text-xs"
                  >
                    {busySlug === l.slug ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      "Switch to this shop"
                    )}
                  </Button>
                )}
                {locations.length > 1 && (
                  <Button
                    data-testid={`locations-delete-${l.slug}`}
                    onClick={() => remove(l)}
                    disabled={busySlug === l.slug}
                    variant="ghost"
                    className="rounded-full text-rose-300 hover:text-rose-200 hover:bg-rose-500/10 h-8 px-3 text-xs"
                  >
                    <Trash2 className="w-3 h-3 mr-1.5" />
                    Remove
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <AddLocationDialog
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onCreated={async () => {
          setShowAdd(false);
          await refresh();
          onSwitched && (await onSwitched());
        }}
      />
    </div>
  );
};

const AddLocationDialog = ({ open, onClose, onCreated }) => {
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [zip, setZip] = useState("");
  const [phone, setPhone] = useState("");
  const [pickup, setPickup] = useState(true);
  const [delivery, setDelivery] = useState(false);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setName(""); setAddress(""); setZip(""); setPhone(""); setPickup(true); setDelivery(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name || !address) {
      toast.error("Name and address are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/dispensary/locations", {
        name,
        address,
        zip,
        phone,
        pickup,
        delivery,
      });
      toast.success("Location added — now active");
      reset();
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="bg-background border-border"
        data-testid="locations-add-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">
            Add a location
          </DialogTitle>
          <DialogDescription className="text-foreground/60">
            We'll auto-resolve the city &amp; coordinates from the ZIP code.
          </DialogDescription>
        </DialogHeader>
        <form className="space-y-4" onSubmit={submit}>
          <Field label="Shop name">
            <Input
              data-testid="locations-add-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Hometown Buds — Wyomissing"
              autoFocus
            />
          </Field>
          <Field label="Address">
            <Input
              data-testid="locations-add-address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="100 Penn Ave"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="ZIP">
              <Input
                data-testid="locations-add-zip"
                value={zip}
                onChange={(e) => setZip(e.target.value)}
                placeholder="19611"
                maxLength={5}
              />
            </Field>
            <Field label="Phone">
              <Input
                data-testid="locations-add-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="610-555-0100"
              />
            </Field>
          </div>
          <div className="flex items-center justify-between gap-3 border-y border-border py-3">
            <div className="flex items-center gap-3">
              <Switch
                data-testid="locations-add-pickup"
                checked={pickup}
                onCheckedChange={setPickup}
              />
              <span className="text-sm">Pickup</span>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                data-testid="locations-add-delivery"
                checked={delivery}
                onCheckedChange={setDelivery}
              />
              <span className="text-sm">Delivery</span>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="locations-add-submit"
              disabled={busy}
              className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {busy ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Create location"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
      {label}
    </Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
